/* eslint-disable no-unused-vars */
import { action, observable, computed, toJS, makeObservable } from 'mobx';
import axios from 'axios';
import ExifReader from 'exifreader';
import _ from 'lodash';

import DeleteStore from './DeleteStore';
import PasswordStore from './PasswordStore';
import ContactStore from './ContactStore';
import NewsletterStore from './NewsletterStore';
import CompanyStore from './CompanyStore';
import CoursesStore from './CoursesStore';
import DevicesStore from './DevicesStore';
import UserActivitiesStore from './UserActivitiesStore';
import ServiceStatusesStore from './ServiceStatusesStore';
import config from '../config';
import { COMPANY_UID_NOT_FOUND, END_USERS_ROLE } from '../utils/constants';
import { BRANCH_NOT_FOUND } from '../pages/ProfilePage/constants';
import { user_is_last_company_admin } from '../exceptions/companyAdmin';
import {
  getCompanyAdminStatus,
  getCompanyAndBranchStatuses,
  getFullAddress,
  isUserCompanyOrBranchOwner,
} from '../utils/helpers';
import { errorAlert, successAlert } from '../utils/alerts';
import { getCompanyQueryParams } from '../utils/routes';

import { companyFactory } from '../pages/ProfilePage/factory/companyFactory';
import makeBranchItem from '../pages/ProfilePage/helper/makeBranchItem';

const { REACT_APP_API_URL } = process.env;

export const getUserMetaDataFromToken = values => {
  return values['https://id.feller.ch/user_metadata'] || {};
};

class ProfileStore {
  constructor(rootStore) {
    makeObservable(this, {
      connection: observable,
      profileData: observable,
      profileUpdateError: observable,
      accountDeleteError: observable,
      companyStatus: observable,
      branchStatus: observable,
      company: observable,
      branch: observable,
      companyAddress: observable,
      branchAddress: observable,
      isOwner: observable,
      isSuperAdmin: observable,
      userRoles: observable,
      formDataToSave: observable,
      currentCompanyManaged: observable,
      loading: observable,
      profileDataToSave: observable,
      companyData: observable,
      initProfile: action,
      saveProfileSuccess: action.bound,
      saveProfile: action,
      updateAvatarImage: action,
      removeAvatarImage: action,
      avatarUpdated: action.bound,
      isNavigatedFromMobile: computed,
      deleteAccount: action,
      extractExifRotation: action,
      checkIsSuperAdmin: action,
      loadUser: action,
      loadCompanyData: action
    });

    this.rootStore = rootStore;

    this.deleteStore = new DeleteStore(this);
    this.passwordStore = new PasswordStore(this);
    this.contactStore = new ContactStore(this);
    this.newsletterStore = new NewsletterStore(this);
    this.companyStore = new CompanyStore(this);
    this.coursesStore = new CoursesStore(this);
    this.devicesStore = new DevicesStore(this);
    this.userActivitiesStore = new UserActivitiesStore(this);
    this.serviceStatusesStore = new ServiceStatusesStore(this);
    this.saveProfile = this.saveProfile.bind(this);
    this.deleteAccount = this.deleteAccount.bind(this);
  }

  tokenPromise = null;
  authToken = null;
  user = null;
  i18n = null;

  connection = null;

  profileData = null;

  profileUpdateError = null;

  accountDeleteError = null;

  companyStatus = null;

  branchStatus = null;

  company = null;

  branch = null;

  companyAddress = null;

  branchAddress = null;

  isOwner = false;

  isSuperAdmin = false;

  userRoles = [];

  formDataToSave = null;

  currentCompanyManaged = false;

  loading = true;

  profileDataToSave = null;

  companyData = {};

  async initProfile(values, authToken, i18n) {
    const userMetadata = getUserMetaDataFromToken(values);
    this.userRoles = this.getUserRolesToken(values);
    this.user = values;
    this.tokenPromise = authToken;

    if (userMetadata.marketingUpdates) {
      userMetadata.marketingUpdates = userMetadata.marketingUpdates === 'true';
    }

    this.profileData = {
      email: values.email,
      picture: values.picture,
      ...userMetadata,
    };

    if (userMetadata.user_type) {
      this.connection = userMetadata.user_type;
      this.connRetrieved = true;
    }

    if (i18n) {
      this.i18n = i18n;
      await this.i18n.changeLanguage(this.profileData.language);
    }
  }

  async getAuthToken() {
    if (!this.authToken) {
      this.authToken = await this.tokenPromise;
    }

    return this.authToken;
  }

  getUserRolesToken(values) {
    return values['https://id.feller.ch/roles'] || [];
  }

  saveProfileSuccess(message) {
    successAlert(message);
    this.rootStore.sendUpdateNotification();
  }

  async saveProfile(values, t, isNewsletters = false) {
    const dataToSave = {
      ...values,
    };
    delete dataToSave.companyData;
    delete dataToSave.userFunction;

    if (values.companyData) {
      let branchValue = _.get(values, 'company.branch.value', null);
      let branch = _.get(values, 'company.branch', null);
      const companyData = _.get(values, 'companyData', null);

      // don't save branch info if hq selected
      if (branch && branch.hq === true) {
        branchValue = null;
      }

      if (branchValue === BRANCH_NOT_FOUND || branch === BRANCH_NOT_FOUND) {
        branchValue = { name: BRANCH_NOT_FOUND };
      }

      dataToSave.company = {
        name: companyData.label,
        uid: companyData.uid,
        uri: companyData.uri,
        branch: toJS(branchValue),
      };
    }

    if (values.company?.uid === COMPANY_UID_NOT_FOUND) {
      this.companyData = companyFactory.getCompanyAdminStatus(false);
    }

    if (values.branch) {
      dataToSave.branch = values.branch ? values.branch.value : null;
    }

    if (values.userFunction) {
      dataToSave.function = values.userFunction;
    }

    if (values.marketingUpdates || values.marketingUpdates === false) {
      dataToSave.marketingUpdates = values.marketingUpdates ? 'true' : 'false';
    }

    delete dataToSave.companyData;
    delete dataToSave.userFunction;
    dataToSave.companyUID = null;
    dataToSave.connection = this.connection;

    const authToken = await this.getAuthToken();

    try {
      const result = await axios.patch(
        `${REACT_APP_API_URL}/me`,
        { user_metadata: dataToSave },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      !isNewsletters && this.saveProfileSuccess(t('profile.updateSuccess'));

      if (dataToSave.language && dataToSave.language !== this.profileData.language) {
        this.profileData.language = dataToSave.language;
        await this.i18n.changeLanguage(dataToSave.language);
      }

      const values = result.data;
      const userMetadata = values['user_metadata'] || {};
      if (userMetadata.companyUID) {
        userMetadata.companyData = {
          value: userMetadata.companyUID,
          label: userMetadata.company,
          uri: userMetadata.uri,
        };
      }

      if (userMetadata.company && typeof userMetadata.company === 'object') {
        userMetadata.companyData = {
          value: userMetadata.company.uid,
          label: userMetadata.company.name,
          uri: userMetadata.uri,
        };

        if (userMetadata.company.branch) {
          userMetadata.company.branch = makeBranchItem(userMetadata.company.branch);
        }
      }

      if (userMetadata.marketingUpdates) {
        userMetadata.marketingUpdates = userMetadata.marketingUpdates === 'true';
      }

      this.profileData = {
        email: values.email,
        picture: values.picture,
        ...userMetadata,
      };

      return true;
    } catch (error) {
      const { data } = error.response;
      if (data.code === user_is_last_company_admin) {
        await this.contactStore.getUsersListIfAdminIsLastCompanyAdmin();
        return (this.contactStore.shouldDisplaySelectSuccessorModal = true);
      }
      errorAlert(t('profile.updateError'));
    }
  }

  async updateAvatarImage(canvas, t) {
    //get blob
    const file = await this.getCanvasBlob(canvas);
    const authToken = await this.getAuthToken();

    try {
      const result = await axios.post(
        `${REACT_APP_API_URL}/upload/sign-upload-url`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const signedUrl = result.data.url;
      await axios.put(signedUrl, file, {
        headers: { 'Content-Type': 'image/png' },
      });

      const newAvatarUrl = signedUrl.split('.png')[0] + '.png';

      await axios.patch(
        `${REACT_APP_API_URL}/me/updateAvatar`,
        { picture: newAvatarUrl },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      this.avatarUpdated(newAvatarUrl);
      this.saveProfileSuccess(t('uploadAvatar.successMessage'));
    } catch (error) {
      errorAlert(t('uploadAvatar.errorMessage'));
    }
  }

  async removeAvatarImage(t) {
    const authToken = await this.getAuthToken();

    try {
      const result = await axios.post(
        `${REACT_APP_API_URL}/me/clearAvatar`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const newAvatarUrl = result.data.picture;
      this.avatarUpdated(newAvatarUrl);

      this.saveProfileSuccess(t('uploadAvatar.removedMessage'));
    } catch (error) {
      errorAlert(t('uploadAvatar.errorMessage'));
    }
  }

  avatarUpdated(newAvatarUrl) {
    const now = new Date().getTime();
    this.profileData.picture = `${newAvatarUrl}?t=${now}`;
  }

  getCanvasBlob(canvas) {
    // eslint-disable-next-line no-unused-vars
    return new Promise(function(resolve) {
      canvas.toBlob(function(blob) {
        resolve(blob);
      }, 'image/png');
    });
  }

  validateProfile(values, t) {
    const errors = {};

    if (!values.firstName || values.firstName.length > 255) {
      errors.firstName = t('firstName.validation');
    }

    if (!values.lastName || values.lastName.length > 255) {
      errors.lastName = t('lastName.validation');
    }

    if (!values.salutation) {
      errors.salutation = t('salutation.validation');
    }

    if (this.connection !== END_USERS_ROLE) {
      if (!values.userFunction) {
        errors.userFunction = t('function.validation');
      }
    }

    return errors;
  }

  get isNavigatedFromMobile() {
    return sessionStorage.getItem('isNavigatedFromMobile') === 'true';
  }

  async deleteAccount(values) {
    const { currentPassword } = values;

    const authToken = await this.getAuthToken();

    const dataToSave = {
      login: this.profileData.email,
      password: currentPassword,
    };

    return await this.deleteStore.deleteAccount(dataToSave, authToken);
  }

  extractExifRotation(file) {
    if (!this.supportsFileReader()) return 0;

    try {
      const result = Promise.resolve(this.readFileAsync(file));

      if (result && result.Orientation) {
        if (result.Orientation.value === 8) {
          return 270;
        }

        if (result.Orientation.value === 6) {
          return 90;
        }

        if (result.Orientation.value === 3) {
          return 180;
        }
      }
      return 0;
    } catch (error) {
      return 0;
    }
  }

  supportsFileReader() {
    return window.FileReader !== undefined;
  }

  readFileAsync(file) {
    if (file) {
      return new Promise((resolve, reject) => {
        let reader = new FileReader();

        reader.onload = readerEvent => {
          try {
            var tags = ExifReader.load(readerEvent.target.result);
            resolve(tags);
          } catch (error) {
            resolve({});
          }
        };
        reader.onerror = reject;

        try {
          reader.readAsArrayBuffer(file);
        } catch (error) {
          return {};
        }
      });
    }

    return {};
  }

  checkIsSuperAdmin = () => {
    this.isSuperAdmin = this.user['https://id.feller.ch/roles'].filter(r => config.portalRoles.includes(r)).length > 0;
  };

  loadUser = async () => {
    try {
      if (this.connection === END_USERS_ROLE) {
        return;
      }

      const authToken = await this.getAuthToken();

      const { data } = await axios.get(`${REACT_APP_API_URL}/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      this.currentCompanyManaged = getCompanyAdminStatus(data.user.Companies);

      this.company = data.user.Companies[0];

      this.companyAddress = '';
      if (this.company != null) {
        const { street, zip, city } = this.company;
        this.companyAddress = getFullAddress(street, zip, city);
      }

      this.branch = data.user.Branches[0];

      this.branchAddress = '';
      if (this.branch != null) {
        const { street, zip, city } = this.branch;
        this.branchAddress = getFullAddress(street, zip, city);
      }

      const { companyStatus, branchStatus } = getCompanyAndBranchStatuses(this.company, this.branch);
      this.companyStatus = companyStatus;
      this.branchStatus = branchStatus;

      this.isOwner = isUserCompanyOrBranchOwner(this.company, this.branch);

      this.checkIsSuperAdmin();

      return data.user;
    } catch (error) {
      // this.saveProfileError(error)
    }
  };

  loadCompanyData = async (companyUid, companyUri) => {
    try {
      const queryParams = getCompanyQueryParams(companyUid, companyUri);
      if (!queryParams) return {};

      const data = await axios.get(`${REACT_APP_API_URL}/public/company/${queryParams}`);
      this.companyData = data.data;
      return data;
    } catch (error) {
      console.log('error', error);
    }
  };
}

export default ProfileStore;
